/**
 * @file Purchase PPD Photos Photo Viewer Component
 * This photo viewer assumes that a photo is already entitled, and thus, will allow to download.
 * As a part of the requirement, users can only come here from an already purchased photo - not from a face
 * blocked one.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';
import EntitledPhotosPhotoActions from './EntitledPhotosPhotoActions';
import Spinner from '../global/Spinner';
import PurchasePpdItemPurchaseModalContainer from '../../containers/purchase/PurchasePpdItemPurchaseModalContainer';
import PurchasePpdPrintPackageModalContainer from '../../containers/purchase/PurchasePpdPrintPackageModalContainer';
import PurchasePpdIndividualPrintModalContainer from '../../containers/purchase/PurchasePpdIndividualPrintModalContainer';
import { isPpd } from '../../util/album';
import Button from '../global/Button';
import IndividualPhotoWarningModal from './IndividualPhotoWarningModal';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import {
  getPriceWithoutAddons,
  sortMatchedPhotosByFacebox,
} from '../../util/ppd-purchase';
import { formatPriceForDisplay } from '../../util/format-money';
import {
  INDIVIDUAL_PHOTO_NOTICE_MODAL,
  PURCHASE_PPD_TYPE_MODAL,
} from '../../constants/modals';
import AlbumPhotoViewer from '../../apps/pro/views/Album/AlbumPhotoViewer/AlbumPhotoViewer';

class PurchasePpdPhotoViewer extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    photoId: PropTypes.string.isRequired,
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    matchedPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        comparedFaces: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        photo: PropTypes.shape({
          id: PropTypes.string,
          thumbnail_urls: PropTypes.arrayOf(
            PropTypes.shape({
              width: PropTypes.number,
              height: PropTypes.number,
              url: PropTypes.string,
            }),
          ),
        }),
      }),
    ),
    entitledPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    entitledPhotosNo: PropTypes.number,
    hiddenIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    toggleCartItem: PropTypes.func,
    loadingPrice: PropTypes.bool,
    openModal: PropTypes.func,
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
        printSize: PropTypes.shape({
          id: PropTypes.string,
        }),
        printPackage: PropTypes.shape({
          id: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    price: PropTypes.shape({
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_shipping_cost: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_tax: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
  };

  onGoToCheckout = () => {
    const { history, album } = this.props;
    history.push(`/purchase/album/${album.id}/photos/checkout`);
  };

  renderCheckoutButtonText = () => {
    const { cart = [], price, loadingPrice } = this.props;

    if (loadingPrice) {
      return 'Calculating...';
    }

    const itemsAmount = cart.length;
    const photoLabel = pluralize('Item', itemsAmount);
    const priceWithoutAddons = getPriceWithoutAddons(price);
    const formattedPrice = formatPriceForDisplay(priceWithoutAddons, true);

    return itemsAmount ? (
      <span>
        Checkout <br />{' '}
        <span className="text-xs">
          ({itemsAmount} {photoLabel} for ${formattedPrice})
        </span>
      </span>
    ) : (
      'Checkout'
    );
  };

  render() {
    const {
      loading,
      album,
      matchedPhotos,
      entitledPhotos,
      hiddenIdentities,
      toggleCartItem,
      loadingPrice,
      openModal,
      cart,
      ...props
    } = this.props;

    if (loading || !album) {
      return (
        <div className="d-block text-center mt-6">
          <Spinner />
        </div>
      );
    }

    const photos = sortMatchedPhotosByFacebox(
      matchedPhotos.filter(matchedPhoto =>
        entitledPhotos.find(
          entitledPhoto => entitledPhoto.id === matchedPhoto.photo.id,
        ),
      ),
    );

    const navigationPath = `/purchase/album/${album.id}/view`;

    return (
      <Fragment>
        <AlbumPhotoViewer
          albumId={album && album.id}
          navigationPath={navigationPath}
          preferWatermarked
          includeLinkInActions
          forceImageSize
          maxSize={IMAGE_SIZE_CODE.LARGE2X}
          actionClassName="web-gallery-photo-viewer"
          accessor="photo"
          photoActions={photoItem =>
            photoItem && (
              <Fragment>
                <div className="d-flex align-items-center justify-content-center">
                  <EntitledPhotosPhotoActions
                    photo={photoItem.photo}
                    album={album}
                    entitledPhotos={photos}
                    toggleClassName="btn-sm btn-glass m-0"
                    toggleLabel="Menu"
                    xpos="right"
                    dropdownClassName="p-1"
                    className="mr-2"
                    canPurchase
                    onPurchase={() => {
                      openModal(PURCHASE_PPD_TYPE_MODAL, photoItem);
                    }}
                  />
                </div>
              </Fragment>
            )
          }
          photos={photos}
          {...props}
        />

        <IndividualPhotoWarningModal />
        <PurchasePpdItemPurchaseModalContainer
          toggleCartItem={toggleCartItem}
          entitledPhotos={entitledPhotos}
        />
        <PurchasePpdPrintPackageModalContainer
          toggleCartItem={toggleCartItem}
        />
        <PurchasePpdIndividualPrintModalContainer
          toggleCartItem={toggleCartItem}
        />
        <div className="photo-viewer-toolbar">
          {(isPpd(album) || cart.length > 0) && (
            <Button
              btnSize="sm"
              btnBlock
              className={`text-sm px-1 ${cart.length === 0 ? 'disabled' : ''}`}
              onClick={
                cart.length > 0
                  ? this.onGoToCheckout
                  : () => openModal(INDIVIDUAL_PHOTO_NOTICE_MODAL)
              }
              disabled={loadingPrice}
            >
              {this.renderCheckoutButtonText()}
            </Button>
          )}
        </div>
      </Fragment>
    );
  }
}

export default PurchasePpdPhotoViewer;
